import { z } from "zod";
import { defineEvent } from "./_define";
import { baseEnvelopeSchema } from "./_envelope";

/**
 * `price_cents` convention: integer cents, the price the user is/would be
 * charged for one billing period of the *currently selected* product+cadence.
 * Stamped on every event that carries product / billing_period info so
 * dashboards can compute weighted revenue without joining to a price table.
 *
 * `implied_mrr_cents` and `implied_immediate_revenue_cents` mirror the
 * `cta_clicked` / webhook `subscription_created` convention so revenue Sum
 * metrics aggregate uniformly across the funnel. At view time they reflect
 * the default product + billing snapshot the user is currently looking at;
 * the same fields fire again on `cta_clicked` with the selected values.
 */
export const paywallViewed = defineEvent({
  eventName: "paywall_viewed",
  eventVersion: 2,
  valueSchema: z.union([z.string(), z.number()]).optional(),
  metadataSchema: baseEnvelopeSchema
    .extend({
      source: z.string().optional(),
      default_product: z.string().optional(),
      default_billing: z.string().optional(),
      price_cents: z.union([z.string(), z.number()]).optional(),
      implied_mrr_cents: z.union([z.string(), z.number()]).optional(),
      implied_immediate_revenue_cents: z
        .union([z.string(), z.number()])
        .optional(),
      pricing_loaded: z.union([z.string(), z.boolean()]).optional(),
      n_children: z.union([z.string(), z.number()]).optional(),
      subscription_status: z.string().optional(),
      journey_id: z.string().optional(),
      is_first_paywall_view: z.union([z.string(), z.boolean()]).optional(),
    })
    .passthrough(),
});

export const billingCadenceSwitched = defineEvent({
  eventName: "billing_cadence_switched",
  eventVersion: 1,
  valueSchema: z.enum(["monthly", "annual"]),
  metadataSchema: baseEnvelopeSchema
    .extend({
      from_cadence: z.enum(["monthly", "annual"]).optional(),
      to_cadence: z.enum(["monthly", "annual"]).optional(),
      current_product: z.string().optional(),
      price_cents: z.union([z.string(), z.number()]).optional(),
      time_on_paywall_ms: z.union([z.string(), z.number()]).optional(),
      source: z.string().optional(),
      journey_id: z.string().optional(),
    })
    .passthrough(),
});

export const productCardSelected = defineEvent({
  eventName: "product_card_selected",
  eventVersion: 1,
  valueSchema: z.string(),
  metadataSchema: baseEnvelopeSchema
    .extend({
      from_product: z.string().optional(),
      to_product: z.string().optional(),
      current_billing_period: z.string().optional(),
      price_cents: z.union([z.string(), z.number()]).optional(),
      source: z.string().optional(),
      journey_id: z.string().optional(),
    })
    .passthrough(),
});
